import { queryOptions } from "@tanstack/react-query";
import { api, unwrap } from "@/client/lib/api";
import { pickById } from "@/client/lib/pick-by-id";
import type { ListedTask } from "@/features/tasks/schemas";

/** Reads every page of a list that pages by the last id it held. */
async function everyPage<Item>(
  read: (after: string | undefined) => Promise<{
    items: Item[];
    next?: string | undefined;
  }>,
): Promise<Item[]> {
  const items: Item[] = [];
  let after: string | undefined;
  do {
    const page = await read(after);
    items.push(...page.items);
    after = page.next;
  } while (after);
  return items;
}

const readProjects = () =>
  everyPage((after) => unwrap(api.projects.$get({ query: { after } })));

export type ProjectRow = Awaited<ReturnType<typeof readProjects>>[number];
export type ProjectTask = ListedTask;

/** Active and archived together; the tabs, the sidebar and every sort read this one list. */
export const projectsQuery = queryOptions({
  queryKey: ["projects"],
  queryFn: readProjects,
});

/**
 * Every project's list of tasks the working set cannot hold. Not under ["tasks"]: a resync
 * refetches that prefix and must not fail on a project this person can no longer read.
 */
export const EARLIER_TASKS = ["project-earlier"] as const;

async function doneBefore(projectId: string): Promise<ListedTask[]> {
  const items: ListedTask[] = [];
  let before: string | undefined;
  do {
    const page = await unwrap(
      api.tasks.done.$get({ query: { projectId, before } }),
    );
    items.push(...page.items);
    before = page.next ?? undefined;
  } while (before);
  return items;
}

/**
 * A project's tasks outside the working set: those done before its 14 days, or every task of an
 * archived project, which the working set drops. The page shows the working set's own rows first.
 */
export const earlierTasksQuery = (projectId: string, archived: boolean) =>
  queryOptions({
    queryKey: [...EARLIER_TASKS, projectId, archived],
    queryFn: (): Promise<ListedTask[]> =>
      archived
        ? everyPage((after) =>
            unwrap(
              api.projects[":id"].tasks.$get({
                param: { id: projectId },
                query: { after },
              }),
            ),
          )
        : doneBefore(projectId),
  });

/** The working set's rows in the project, then the earlier ones it does not hold. */
export function projectTasks(
  working: readonly ListedTask[],
  earlier: readonly ListedTask[],
  projectId: string,
): ListedTask[] {
  const held = new Set(working.map((row) => row.id));
  return [
    ...working.filter((row) => row.projectId === projectId),
    ...earlier.filter((row) => !held.has(row.id)),
  ];
}

export const clientsQuery = queryOptions({
  queryKey: ["clients"],
  queryFn: () =>
    everyPage((after) => unwrap(api.clients.$get({ query: { after } }))),
});

/**
 * A project has no colour column; its hue is read from its id, so it is the same on every screen
 * and every device. Each pair is a tint and the ink measured on it (`docs/design/system.md`).
 */
const HUES = [
  { tint: "bg-tint-orange", ink: "text-primary-text" },
  { tint: "bg-tint-blue", ink: "text-info" },
  { tint: "bg-tint-green", ink: "text-success" },
  { tint: "bg-tint-teal", ink: "text-ink-teal" },
  { tint: "bg-tint-violet", ink: "text-ink-violet" },
  { tint: "bg-tint-amber", ink: "text-warning" },
] as const;

export function projectHue(projectId: string): (typeof HUES)[number] {
  return pickById(projectId, HUES);
}
